/** Shimmer placeholders. Same footprint as the real content, so nothing jumps. */

import type { CSSProperties } from "react";
import { StatGrid } from "./StatCard";

/**
 * `style` exists for the one case a class cannot cover: a placeholder whose
 * footprint is computed, like the blocks standing in for events on a day grid
 * whose hour height is a constant in TypeScript.
 */
export function Skeleton({ className = "", style }: { className?: string; style?: CSSProperties }) {
  return (
    <div
      className={`animate-pulse rounded-control bg-surface-2 ${className}`}
      style={style}
      aria-hidden="true"
    />
  );
}

/** Chart-shaped skeleton with proportional bars (used inside ChartCard). */
export function ChartSkeleton({ height = 240 }: { height?: number }) {
  const bars = [45, 70, 35, 85, 55, 95, 40, 65, 75, 50, 80, 60];
  return (
    <div className="flex items-end gap-2" style={{ height }} aria-hidden="true">
      {bars.map((h, i) => (
        <div
          key={i}
          className="flex-1 animate-pulse rounded-t-control bg-surface-2"
          style={{ height: `${h}%` }}
        />
      ))}
    </div>
  );
}

interface LoadingSkeletonProps {
  variant?: "chart" | "card" | "lines" | "table" | "stats";
  height?: number;
  rows?: number;
  /** `table` only: column count, so the shimmer matches the real table. */
  cols?: number;
  className?: string;
}

export default function LoadingSkeleton({
  variant = "lines",
  height = 240,
  rows = 4,
  cols = 3,
  className = "",
}: LoadingSkeletonProps) {
  // Screen readers get one spoken "завантаження", not a wall of empty boxes.
  const live = { role: "status" as const, "aria-busy": true, "aria-label": "Завантаження" };

  if (variant === "chart") {
    return (
      <div className={className} {...live}>
        <ChartSkeleton height={height} />
      </div>
    );
  }

  if (variant === "stats") {
    return (
      <StatGrid className={className}>
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="jv-card p-4" {...(i === 0 ? live : {})}>
            <Skeleton className="h-3 w-24" />
            <Skeleton className="mt-3 h-8 w-32" />
            <Skeleton className="mt-3 h-3 w-20" />
          </div>
        ))}
      </StatGrid>
    );
  }

  if (variant === "table") {
    return (
      <div className={`space-y-2 ${className}`} {...live}>
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="flex items-center gap-3">
            <Skeleton className="h-4 flex-1" />
            {Array.from({ length: Math.max(0, cols - 1) }).map((_, j) => (
              <Skeleton key={j} className={j % 2 ? "h-4 w-20" : "h-4 w-16"} />
            ))}
          </div>
        ))}
      </div>
    );
  }

  if (variant === "card") {
    return (
      <div className={`jv-card p-4 ${className}`} {...live}>
        <Skeleton className="h-3 w-28" />
        <Skeleton className="mt-3 h-8 w-40" />
        <Skeleton className="mt-3 h-3 w-24" />
      </div>
    );
  }

  return (
    <div className={`space-y-2 ${className}`} {...live}>
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className="h-4" />
      ))}
    </div>
  );
}
